/**
 * NiV tool — send an FCM push to selected clinics via the
 * sendAdminPushToClinics Cloud Function (nivAdmin only). Error code: CK-9892.
 */

import { useMemo, useState } from "react";
import { httpsCallable } from "firebase/functions";
import {
  Buildings,
  CheckCircle,
  CheckSquare,
  MagnifyingGlass,
  Megaphone,
  MinusSquare,
  PaperPlaneTilt,
  Square,
} from "@phosphor-icons/react";
import LoaderLoop from "../../../../components/shared/loader/LoaderLoop";
import { functions } from "../../../../firebase";
import { useGetNivClinicsOverviewQuery } from "../../../../store/services/nivApi";
import { useToast } from "../../../../contexts/ToastContext";
import ConfirmDialog from "../../../../components/ui/ConfirmDialog";
import ClinicTags from "../shared/ClinicTags";

function NivPushTool({ isAuthorized }) {
  const { showSuccess, showError } = useToast();
  const { data: allRows = [] } = useGetNivClinicsOverviewQuery(undefined, {
    skip: !isAuthorized,
  });
  const clinics = useMemo(() => allRows.filter((r) => !r.inactiveByAdmin), [allRows]);

  const [selected, setSelected] = useState(() => new Set());
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [link, setLink] = useState("/dashboard");
  const [search, setSearch] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clinics;
    return clinics.filter(
      (c) => c.name?.toLowerCase().includes(q) || c.ownerName?.toLowerCase().includes(q)
    );
  }, [clinics, search]);

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((c) => selected.has(c.clinicId));

  const toggleOne = (clinicId) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(clinicId)) next.delete(clinicId);
      else next.add(clinicId);
      return next;
    });

  const toggleAll = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) filtered.forEach((c) => next.delete(c.clinicId));
      else filtered.forEach((c) => next.add(c.clinicId));
      return next;
    });

  const canSend = selected.size > 0 && title.trim() && body.trim() && !sending;

  const handleSend = async () => {
    setConfirmOpen(false);
    if (!canSend) return;
    setSending(true);
    setResult(null);
    try {
      const sendPush = httpsCallable(functions, "sendAdminPushToClinics");
      const res = await sendPush({
        clinicIds: Array.from(selected),
        title: title.trim(),
        body: body.trim(),
        link: link.trim() || "/dashboard",
      });
      setResult(res.data);
      showSuccess(
        `Push נשלח ל-${res.data.clinicCount} קליניקות (${res.data.totalSuccess} הצלחות)`
      );
      setTitle("");
      setBody("");
      setSelected(new Set());
    } catch (error) {
      console.error("[CK-9892] Admin push send failed:", {
        errorCode: "CK-9892",
        errorMessage: error?.message,
        clinicCount: selected.size,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשליחת Push");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="niv-push-content">
      <div className="niv-push-header">
        <h3>
          <Megaphone size={22} weight="fill" />
          שליחת Push לקליניקות
        </h3>
        <span className="niv-push-selected-count">
          {selected.size} נבחרו מתוך {clinics.length}
        </span>
      </div>

      <div className="niv-push-form">
        <div className="niv-push-form-group">
          <label className="niv-push-label" htmlFor="niv-push-title">כותרת ההודעה *</label>
          <input
            id="niv-push-title"
            type="text"
            className="niv-push-input"
            placeholder='למשל: "עדכון חדש במערכת"'
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={100}
          />
        </div>
        <div className="niv-push-form-group">
          <label className="niv-push-label" htmlFor="niv-push-body">תוכן ההודעה *</label>
          <textarea
            id="niv-push-body"
            className="niv-push-textarea"
            placeholder='למשל: "הוספנו אפשרות חדשה ליומן - בואו לבדוק!"'
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            maxLength={300}
          />
        </div>
        <div className="niv-push-form-group">
          <label className="niv-push-label" htmlFor="niv-push-link">קישור (לאן לנווט בלחיצה)</label>
          <input
            id="niv-push-link"
            type="text"
            className="niv-push-input"
            placeholder="/dashboard"
            value={link}
            onChange={(e) => setLink(e.target.value)}
          />
        </div>

        <button
          type="button"
          className="niv-btn niv-btn-primary niv-push-send-btn"
          onClick={() => setConfirmOpen(true)}
          disabled={!canSend}
        >
          {sending ? (
            <>
              <LoaderLoop size="xs" /> שולח...
            </>
          ) : (
            <>
              <PaperPlaneTilt size={18} weight="fill" /> שלח Push ל-{selected.size} קליניקות
            </>
          )}
        </button>

        {result && (
          <div className="niv-push-result">
            <CheckCircle size={18} weight="fill" />
            <span>
              נשלח ל-{result.clinicCount} קליניקות — {result.totalSuccess} הצלחות,{" "}
              {result.totalFailure} כשלונות
            </span>
          </div>
        )}
      </div>

      <div className="niv-push-clinics-section">
        <div className="niv-push-clinics-toolbar">
          <div className="niv-search-box">
            <MagnifyingGlass size={18} />
            <input
              type="text"
              placeholder="חיפוש קליניקה..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="חיפוש קליניקה"
            />
          </div>
          <button type="button" className="niv-btn niv-btn-secondary niv-btn-sm" onClick={toggleAll}>
            {allFilteredSelected ? (
              <>
                <MinusSquare size={16} weight="bold" /> בטל הכל
              </>
            ) : (
              <>
                <CheckSquare size={16} weight="bold" /> בחר הכל ({filtered.length})
              </>
            )}
          </button>
        </div>

        <div className="niv-push-clinics-list">
          {filtered.length === 0 ? (
            <div className="niv-empty-state" style={{ minHeight: 150 }}>
              <Buildings size={32} weight="light" />
              <p>לא נמצאו קליניקות</p>
            </div>
          ) : (
            filtered.map((clinic) => {
              const isSelected = selected.has(clinic.clinicId);
              return (
                <button
                  type="button"
                  key={clinic.clinicId}
                  className={`niv-push-clinic-row ${isSelected ? "selected" : ""}`}
                  onClick={() => toggleOne(clinic.clinicId)}
                  aria-pressed={isSelected}
                >
                  <div className="niv-push-clinic-checkbox">
                    {isSelected ? (
                      <CheckSquare size={20} weight="fill" className="niv-push-check-on" />
                    ) : (
                      <Square size={20} weight="regular" className="niv-push-check-off" />
                    )}
                  </div>
                  <div className="niv-push-clinic-info">
                    <span className="niv-push-clinic-name">{clinic.name || "ללא שם"}</span>
                    <span className="niv-push-clinic-owner">{clinic.ownerName || "—"}</span>
                  </div>
                  <ClinicTags row={clinic} compact />
                </button>
              );
            })
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleSend}
        title="שליחת Push"
        message={`ההודעה תישלח ל-${selected.size} קליניקות. להמשיך?`}
        confirmText="שלח"
        cancelText="ביטול"
        type="info"
      />
    </div>
  );
}

export default NivPushTool;
